import { lazy, Suspense, useEffect, useState } from "react";
import { Ghost } from "lucide-react";
import { useLocation } from "react-router-dom";
import { usePlatformStatus } from "@/contexts/PlatformStatusContext";
import { useAdminRole } from "@/hooks/useAdminRole";

const GhostChatPanel = lazy(() =>
  import("./GhostChatPanel").then((m) => ({ default: m.GhostChatPanel }))
);

const BUBBLE_DISMISSED_KEY = "ghost_bubble_dismissed";

export const GhostLauncher = () => {
  const [open, setOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const { maintenanceMode } = usePlatformStatus();
  const { isAdmin } = useAdminRole();
  const location = useLocation();

  // Show "Chat Now" bubble after a short delay, unless previously dismissed.
  useEffect(() => {
    const dismissed = sessionStorage.getItem(BUBBLE_DISMISSED_KEY) === "1";
    if (dismissed || open) return;
    const t = setTimeout(() => setShowBubble(true), 2500);
    return () => clearTimeout(t);
  }, [open]);

  // Hide during maintenance for non-admins, on auth pages, or while no route mounted.
  const hidden =
    (maintenanceMode && !isAdmin) ||
    location.pathname === "/auth" ||
    location.pathname === "/reset-password" ||
    location.pathname === "/waiting-list";

  if (hidden) return null;

  const handleOpen = () => {
    setOpen(true);
    setShowBubble(false);
    sessionStorage.setItem(BUBBLE_DISMISSED_KEY, "1");
  };

  return (
    <>
      {!open && (
        <div className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[55] flex items-center gap-2">
          {showBubble && (
            <button
              onClick={handleOpen}
              className="relative flex items-center gap-1.5 bg-background border border-primary/60 text-foreground
                         text-[11px] sm:text-xs font-medium px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-full shadow-lg shadow-primary/20
                         animate-fade-in hover:bg-primary hover:text-primary-foreground transition-colors
                         max-w-[calc(100vw-5rem)] whitespace-nowrap"
              aria-label="Chat with Ghost now"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              Chat Now
              {/* tail */}
              <span className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rotate-45
                               bg-background border-r border-t border-primary/60" />
            </button>
          )}
          <button
            onClick={handleOpen}
            aria-label="Open Ghost chat"
            className="group relative w-14 h-14 rounded-full bg-background border border-primary/60
                       flex items-center justify-center shadow-xl shadow-primary/20
                       hover:bg-primary hover:scale-105 transition-all duration-200
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Ghost className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-colors" />
            <span className="absolute inset-0 rounded-full ring-2 ring-primary/30 animate-ping opacity-40 pointer-events-none" />
          </button>
        </div>
      )}

      {open && (
        <Suspense fallback={null}>
          <GhostChatPanel onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </>
  );
};
